import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUsernameCheck } from '@/hooks/useUsernameCheck';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, BookOpen, Check, AlertCircle, Camera, Users } from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { DinoAvatar } from '@/components/DinoAvatar';
import { AvatarEditor } from '@/components/AvatarEditor';
import { DateOfBirthInput } from '@/components/DateOfBirthInput';
import { cn } from '@/lib/utils';

const onboardingSchema = z.object({
  username: z.string()
    .min(3, 'Username must be at least 3 characters')
    .max(20, 'Username must be at most 20 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
  bio: z.string()
    .max(160, 'Bio must be at most 160 characters')
    .optional(),
  dateOfBirth: z.date()
    .refine((date) => {
      const today = new Date();
      const minDate = new Date(1900, 0, 1);
      return date >= minDate && date <= today;
    }, 'Please enter a valid date of birth')
    .refine((date) => {
      const today = new Date();
      const age = today.getFullYear() - date.getFullYear() - 
        (today < new Date(today.getFullYear(), date.getMonth(), date.getDate()) ? 1 : 0);
      return age >= 13;
    }, 'You must be at least 13 years old to join Dino'),
});

type OnboardingForm = z.infer<typeof onboardingSchema>;

const stockAvatars = [
  { id: 'book', src: '/avatars/open-book.svg', label: 'Open Book' },
  { id: 'stack', src: '/avatars/stack-books.svg', label: 'Book Stack' },
  { id: 'shelf', src: '/avatars/bookshelf.svg', label: 'Bookshelf' },
];

export default function OnboardingProfile() {
  const { user, updateProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(false);
  const [step, setStep] = useState(1);
  const [ageWarning, setAgeWarning] = useState<string | null>(null);
  const totalSteps = 4; // Added DOB step

  // Check if in retake mode
  const isRetakeMode = sessionStorage.getItem('retakeOnboarding') === '1' || 
                       searchParams.get('retake') === '1';

  const form = useForm<OnboardingForm>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      username: '',
      bio: '',
      dateOfBirth: undefined,
    },
    mode: 'onChange',
  });

  const username = form.watch('username');
  const { isAvailable, isChecking, error: usernameError } = useUsernameCheck(username, user?.id);

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) throw error;

        setProfile(data);

        // If user already has username and avatar, redirect to next step (unless retaking)
        if (data.username && data.image && !isRetakeMode) {
          navigate('/onboarding/import', { replace: true });
          return;
        }

        // Set initial values if available
        if (data.display_name) {
          form.setValue('username', data.display_name.toLowerCase().replace(/[^a-zA-Z0-9]/g, ''));
        }
        if (data.bio) {
          form.setValue('bio', data.bio);
        }
        if (data.image) {
          setSelectedAvatar(data.image);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data. Please refresh and try again.",
          variant: "destructive",
        });
      }
    };

    if (user && !authLoading) {
      loadProfile();
    }
  }, [user, authLoading, navigate, form]);

  // Redirect if no user or loading
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate('/auth', { replace: true });
    return null;
  }

  const isUsernameValid = username.length >= 3 && isAvailable === true;
  const canContinue = isUsernameValid && !isChecking;

  const handleAvatarUpdated = (newAvatarUrl: string) => {
    setSelectedAvatar(newAvatarUrl);
    setAvatarEditorOpen(false);
  };

  const handleStockAvatarSelect = (avatarSrc: string) => {
    setSelectedAvatar(avatarSrc);
  };

  const handleSubmit = async (data: OnboardingForm) => {
    if (!canContinue || !guidelinesAccepted) return;

    setLoading(true);
    try {
      // Calculate age to determine account status
      const dob = data.dateOfBirth;
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear() - 
        (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);

      // Update non-sensitive user data
      const updates: any = {
        username: data.username,
        bio: data.bio || null,
        guidelines_accepted_at: new Date().toISOString(),
      };

      // Only add image if one was selected
      if (selectedAvatar) {
        updates.image = selectedAvatar;
      }

      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      // Auto-detect and persist timezone silently
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (detectedTimezone) {
        await supabase
          .from('profiles')
          .update({ timezone: detectedTimezone })
          .eq('id', user.id);
      }

      // Store sensitive data (DOB) in separate secure table via RPC
      const dobString = dob.toISOString().split('T')[0]; // YYYY-MM-DD format
      const { error: sensitiveError } = await supabase
        .rpc('upsert_user_sensitive_data', {
          p_date_of_birth: dobString,
          p_account_status: age >= 13 && age <= 15 ? 'minor_private' : 'standard'
        });

      if (sensitiveError) throw sensitiveError;

      // Update auth context
      if (updateProfile) {
        await updateProfile(updates);
      }

      // Show age-appropriate message
      let toastMessage = "Would you like to import your reading data?";
      if (age >= 13 && age <= 15) {
        toastMessage = "Your account is private by default for your safety. You can make it public when you turn 16!";
      }

      toast({
        title: "Profile setup complete!",
        description: toastMessage,
      });

      navigate('/onboarding/import', { replace: true });
    } catch (error: any) {
      console.error('Onboarding error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete onboarding. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg card-elevated">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center">
            <BookOpen className="w-8 h-8 text-primary-foreground" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-heading">Set up your profile</CardTitle>
            <CardDescription>
              Join a community of readers and share your adventure.
            </CardDescription>
          </div>
          <div className="flex items-center justify-center gap-2 p-3 rounded-lg border bg-muted/50 max-w-md mx-auto">
            <Users className="w-5 h-5 text-primary flex-shrink-0" />
            <p className="text-sm font-medium">Turn your reading journey into a shared adventure!</p>
          </div>
          <Progress value={(step / totalSteps) * 100} className="w-full" />
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            {/* Step 1: Username */}
            {step === 1 && (
              <div className="space-y-4 animate-fade-in">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">Choose your username</h3>
                  <p className="text-sm text-muted-foreground">
                    This will be your unique identifier on Dino.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    placeholder="Enter your username"
                    {...form.register('username')}
                    disabled={loading}
                    className={cn(
                      "transition-colors",
                      username.length >= 3 && isAvailable === true && "border-success",
                      username.length >= 3 && isAvailable === false && "border-destructive"
                    )}
                  />
                  
                  <div className="flex items-center gap-2 min-h-[20px]">
                    {isChecking && username.length >= 3 && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Checking availability...
                      </div>
                    )}
                    
                    {!isChecking && username.length >= 3 && isAvailable === true && (
                      <div className="flex items-center gap-2 text-sm text-success">
                        <Check className="w-3 h-3" />
                        Username is available
                      </div>
                    )}
                    
                    {!isChecking && username.length >= 3 && isAvailable === false && (
                      <div className="flex items-center gap-2 text-sm text-destructive">
                        <AlertCircle className="w-3 h-3" />
                        Username is taken
                      </div>
                    )}
                    
                    {usernameError && (
                      <div className="flex items-center gap-2 text-sm text-destructive">
                        <AlertCircle className="w-3 h-3" />
                        {usernameError}
                      </div>
                    )}
                  </div>

                  {form.formState.errors.username && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.username.message}
                    </p>
                  )}

                  <div className="text-xs text-muted-foreground">
                    3-20 characters, letters, numbers, and underscores only
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={nextStep}
                  className="w-full h-12"
                  disabled={!canContinue}
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Avatar */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">Choose your avatar</h3>
                  <p className="text-sm text-muted-foreground">
                    Pick an image or icon to represent you.
                  </p>
                </div>

                {/* Current Avatar Preview */}
                <div className="flex justify-center">
                  <div className="relative">
                    <DinoAvatar
                      src={selectedAvatar || undefined}
                      name={form.getValues('username') || user?.email}
                      size="xl"
                      className="ring-2 ring-butter ring-offset-2"
                    />
                  </div>
                </div>

                {/* Avatar Options */}
                <div className="space-y-4">
                  {/* Upload Photo */}
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 text-left justify-start"
                    onClick={() => setAvatarEditorOpen(true)}
                  >
                    <Camera className="w-5 h-5 mr-3" />
                    <div className="text-left">
                      <div className="font-medium">Upload a photo</div>
                      <div className="text-xs text-muted-foreground">JPG, PNG, or WebP</div>
                    </div>
                  </Button>

                  <Separator />

                  {/* Stock Icons */}
                  <div className="space-y-3">
                    <Label>Or choose an icon</Label>
                    <div className="grid grid-cols-3 gap-3">
                      {stockAvatars.map((avatar) => (
                        <button
                          key={avatar.id}
                          type="button"
                          className={cn(
                            "flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors hover:bg-muted/50",
                            selectedAvatar === avatar.src 
                              ? "border-primary bg-primary/5" 
                              : "border-border"
                          )}
                          onClick={() => handleStockAvatarSelect(avatar.src)}
                        >
                          <DinoAvatar src={avatar.src} size="lg" />
                          <span className="text-xs font-medium">{avatar.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1 h-12"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="flex-1 h-12"
                  >
                    {selectedAvatar ? 'Continue' : 'Skip for now'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Date of Birth */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">When were you born?</h3>
                  <p className="text-sm text-muted-foreground">
                    This helps us keep Dino safe and age-appropriate.
                  </p>
                </div>

                <Controller
                  name="dateOfBirth"
                  control={form.control}
                  render={({ field }) => (
                    <DateOfBirthInput
                      value={field.value}
                      onChange={field.onChange}
                      error={form.formState.errors.dateOfBirth?.message}
                    />
                  )}
                />

                {ageWarning && (
                  <Alert className="bg-secondary/5 border-secondary">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{ageWarning}</AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1 h-12"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      const dob = form.getValues('dateOfBirth');
                      if (dob) {
                        const today = new Date();
                        const age = today.getFullYear() - dob.getFullYear() - 
                          (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                        
                        if (age >= 13 && age <= 15) {
                          setAgeWarning("Your account will be private by default for your safety. You'll be able to make it public when you turn 16.");
                        }
                        nextStep();
                      }
                    }}
                    className="flex-1 h-12"
                    disabled={!form.watch('dateOfBirth')}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Bio */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-semibold">Tell us about yourself</h3>
                  <p className="text-sm text-muted-foreground">
                    Share a bit about your reading interests (optional)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="I love reading sci-fi novels and cozy mysteries..."
                    {...form.register('bio')}
                    disabled={loading}
                    rows={4}
                  />
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span>Optional</span>
                    <span>{form.watch('bio')?.length || 0}/160</span>
                  </div>
                  {form.formState.errors.bio && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.bio.message}
                    </p>
                  )}
                </div>

                {/* Profile Summary */}
                <Card className="bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <DinoAvatar 
                        src={selectedAvatar || undefined}
                        name={form.getValues('username') || user?.email}
                        size="lg"
                        className="ring-2 ring-butter ring-offset-2"
                      />
                      <div className="flex-1 space-y-1">
                        <div className="font-semibold">@{form.getValues('username')}</div>
                        <div className="text-sm text-muted-foreground">
                          {form.getValues('bio') || 'No bio yet'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Community Guidelines Agreement */}
                <Card className="bg-butter/5 border-butter/20">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0 mt-0.5">🦖</span>
                      <div className="flex-1 space-y-2">
                        <p className="text-sm font-medium">Dino Community Promise</p>
                        <p className="text-xs text-muted-foreground">
                          Dino is a cosy corner for readers to share, discover, and stay accountable — not a place for drama.
                        </p>
                        <ul className="text-xs text-muted-foreground space-y-1">
                          <li>📘 Be kind and respectful to others</li>
                          <li>📗 Keep reviews honest and constructive</li>
                          <li>📙 Post content that fits our bookish vibe</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3 pt-2">
                      <Checkbox
                        id="guidelines"
                        checked={guidelinesAccepted}
                        onCheckedChange={(checked) => setGuidelinesAccepted(checked === true)}
                      />
                      <div className="space-y-1 leading-none">
                        <Label
                          htmlFor="guidelines"
                          className="text-sm font-medium cursor-pointer"
                        >
                          I agree to follow Dino's Community Guidelines
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          You can read the full guidelines{' '}
                          <Link 
                            to="/community-guidelines" 
                            target="_blank"
                            className="text-primary hover:underline"
                          >
                            here
                          </Link>
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="flex-1 h-12"
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12"
                    disabled={loading || !canContinue || !guidelinesAccepted}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      'Get Started'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Avatar Editor Modal */}
      <AvatarEditor
        isOpen={avatarEditorOpen}
        onClose={() => setAvatarEditorOpen(false)}
        currentAvatar={selectedAvatar}
        onAvatarUpdated={handleAvatarUpdated}
      />
    </div>
  );
}